'use client';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Full-width radio rows: a real circle, a label, a line of explanation, and a
 * right-aligned value. The fine choice that sits under a ChoiceCards group
 * ("which of these à-vista methods?"), where the amount is part of the choice.
 *
 * The value column is why this is not a plain radio list: a payer comparing
 * methods is comparing prices, and a price that is not on the same line as the
 * option it belongs to makes them do the matching in their head.
 */
export type ChoiceRowOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  hint?: React.ReactNode;
  /** Right-aligned, usually the total for this method. */
  trailing?: React.ReactNode;
  disabled?: boolean;
};

export function ChoiceRows<T extends string>({
  value,
  onChange,
  options,
  className,
  'aria-label': ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ChoiceRowOption<T>[];
  className?: string;
  'aria-label': string;
}) {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, delta: number) => {
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    if (enabled.length === 0) return;
    const pos = enabled.indexOf(from);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('flex flex-col gap-2', className)}>
      {options.map((option, i) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                move(i, 1);
              }
              if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                move(i, -1);
              }
            }}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
              'focus-visible:border-medlux focus-visible:shadow-focus focus-visible:outline-none',
              selected
                ? 'border-medlux bg-medlux-soft'
                : 'border-border bg-white hover:border-medlux/40',
              option.disabled && 'cursor-not-allowed opacity-50 hover:border-border',
            )}
          >
            {/* The circle is decorative: role="radio" + aria-checked already
                carry the state to assistive tech. */}
            <span
              aria-hidden
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                selected ? 'border-medlux' : 'border-neutral-300',
              )}
            >
              {selected && <span className="h-2.5 w-2.5 rounded-full bg-medlux" />}
            </span>

            <span className="min-w-0 flex-1">
              <span
                className={cn(
                  'block text-sm font-semibold',
                  selected ? 'text-medlux-soft-foreground' : 'text-foreground',
                )}
              >
                {option.label}
              </span>
              {option.hint && (
                <span className="mt-0.5 block text-[0.8125rem] leading-snug text-muted-foreground">
                  {option.hint}
                </span>
              )}
            </span>

            {option.trailing && (
              <span className="shrink-0 text-sm font-bold text-foreground">{option.trailing}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
